import { Injectable, inject, signal, computed, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, of, forkJoin } from 'rxjs';
import { tap, map, catchError } from 'rxjs/operators';
import {
  UploadRepositoryService,
  PatientUploadsRepositoryService,
  Upload,
  NewUpload,
  UpdateUpload,
  TotalStorageUsageResponse,
  VerifyStorageAvailableResponse,
  BaseTag,
  WebSocketService,
  Events
} from '@xmedical/core';

/**
 * UploadsStore
 *
 * Library-owned store for managing upload state across the application.
 * Uses Angular signals for reactive state management.
 *
 * Data managed:
 * - Patient uploads (cached by patientId)
 * - Practice-wide storage usage
 * - Recently queried tags for autocomplete
 *
 * This store:
 * - Preloads and caches patient uploads to avoid re-fetching when switching views
 * - Invalidates cache on upload/delete/update events
 * - Integrates with WebSocket events for real-time updates
 * - Reduces polling frequency by using cached data
 */
@Injectable({
  providedIn: 'root'
})
export class UploadsStore {
  private readonly uploadRepo = inject(UploadRepositoryService);
  private readonly patientUploadsRepo = inject(PatientUploadsRepositoryService);
  private readonly websocket = inject(WebSocketService);
  private readonly destroyRef = inject(DestroyRef);

  // ============================================================
  // State Signals
  // ============================================================

  /**
   * Patient uploads cached by patientId
   * Key: patientId, Value: Upload[]
   */
  private readonly _patientUploadsMap = signal<Map<number, Upload[]>>(new Map());

  /**
   * Practice-wide storage usage metrics
   */
  readonly storageUsage = signal<TotalStorageUsageResponse | null>(null);

  /**
   * Recently queried tags for autocomplete
   * Limited to last 50 unique tags
   */
  readonly recentTags = signal<BaseTag[]>([]);

  // ============================================================
  // Loading State Signals
  // ============================================================

  readonly loadingStorageUsage = signal<boolean>(false);
  readonly loadingPatientUploads = signal<Map<number, boolean>>(new Map());
  readonly storageUsageLoaded = signal<boolean>(false);

  // ============================================================
  // WebSocket Channel Tracking
  // ============================================================

  private subscribedPatientChannels = new Set<string>();
  private practiceChannelName = '';

  // ============================================================
  // Computed Signals
  // ============================================================

  /**
   * Check if storage usage has been loaded
   */
  readonly hasStorageUsage = computed(() => this.storageUsage() !== null);

  /**
   * Get storage usage percentage (0-100)
   */
  readonly storageUsedPercentage = computed(() =>
    this.storageUsage()?.storageUsedPercentage ?? 0
  );

  /**
   * Check if storage is near capacity (>90%)
   */
  readonly isStorageNearCapacity = computed(() =>
    this.storageUsedPercentage() > 90
  );

  /**
   * Get all cached patient IDs
   */
  readonly cachedPatientIds = computed(() =>
    Array.from(this._patientUploadsMap().keys())
  );

  // ============================================================
  // Public Accessor Methods
  // ============================================================

  /**
   * Get uploads for a specific patient
   * Returns empty array if not cached
   *
   * @param patientId - The patient ID
   * @returns Upload array (current snapshot)
   */
  getPatientUploads(patientId: number): Upload[] {
    return this._patientUploadsMap().get(patientId) ?? [];
  }

  /**
   * Check if patient uploads are cached
   *
   * @param patientId - The patient ID
   * @returns boolean
   */
  hasPatientUploads(patientId: number): boolean {
    return this._patientUploadsMap().has(patientId);
  }

  /**
   * Check if patient uploads are currently loading
   *
   * @param patientId - The patient ID
   * @returns boolean
   */
  isLoadingPatientUploads(patientId: number): boolean {
    return this.loadingPatientUploads().get(patientId) ?? false;
  }

  // ============================================================
  // Storage Usage Methods
  // ============================================================

  /**
   * Load practice-wide storage usage
   * Only fetches if not already loaded or force refresh requested
   *
   * @param forceRefresh - Force reload even if cached
   * @returns Observable<TotalStorageUsageResponse>
   */
  loadStorageUsage(forceRefresh = false): Observable<TotalStorageUsageResponse> {
    if (!forceRefresh && this.storageUsageLoaded() && this.storageUsage()) {
      return of(this.storageUsage()!);
    }

    this.loadingStorageUsage.set(true);

    return this.uploadRepo.getTotalStorageUsage().pipe(
      tap(usage => {
        this.storageUsage.set(usage);
        this.storageUsageLoaded.set(true);
        this.loadingStorageUsage.set(false);
      }),
      catchError(error => {
        console.error('[UploadsStore] Error loading storage usage:', error);
        this.loadingStorageUsage.set(false);
        throw error;
      })
    );
  }

  /**
   * Verify if storage is available for upload
   *
   * @param size - Size in bytes of pending upload
   * @returns Observable<VerifyStorageAvailableResponse>
   */
  verifyStorageAvailable(size: number): Observable<VerifyStorageAvailableResponse> {
    return this.uploadRepo.verifyStorageAvailable(size);
  }

  // ============================================================
  // Patient Uploads Methods
  // ============================================================

  /**
   * Load uploads for a patient
   * Uses cached data if available, otherwise fetches from API
   *
   * @param patientId - The patient ID
   * @param tags - Optional tags to filter by
   * @param forceRefresh - Force reload even if cached
   * @returns Observable<Upload[]>
   */
  loadPatientUploads(
    patientId: number,
    tags: string[] = [],
    forceRefresh = false
  ): Observable<Upload[]> {
    // Return cached data if available and not forcing refresh
    if (!forceRefresh && this.hasPatientUploads(patientId)) {
      const cached = this.getPatientUploads(patientId);
      // Apply tag filter to cached data
      if (tags.length > 0) {
        return of(this.filterUploadsByTags(cached, tags));
      }
      return of(cached);
    }

    // Set loading state
    this.setPatientUploadsLoading(patientId, true);

    return this.patientUploadsRepo.loadAll(patientId, tags).pipe(
      tap(uploads => {
        this.setPatientUploads(patientId, uploads);
        this.setPatientUploadsLoading(patientId, false);
      }),
      catchError(error => {
        console.error(`[UploadsStore] Error loading uploads for patient ${patientId}:`, error);
        this.setPatientUploadsLoading(patientId, false);
        throw error;
      })
    );
  }

  /**
   * Preload uploads for multiple patients
   * Useful for preloading data when user navigates to patient list
   *
   * @param patientIds - Array of patient IDs to preload
   * @returns Observable<void>
   */
  preloadPatientUploads(patientIds: number[]): Observable<void> {
    const uncached = patientIds.filter(id => !this.hasPatientUploads(id));

    if (uncached.length === 0) {
      return of(void 0);
    }

    const loaders = uncached.map(id => this.loadPatientUploads(id));
    return forkJoin(loaders).pipe(map(() => void 0));
  }

  // ============================================================
  // CRUD Operations
  // ============================================================

  /**
   * Create a new upload
   *
   * @param upload - The new upload data
   * @returns Observable<number> - The new upload ID
   */
  createUpload(upload: NewUpload): Observable<number> {
    return this.patientUploadsRepo.create(upload).pipe(
      tap(() => {
        // Invalidate patient cache to trigger reload
        if (upload.patientId) {
          this.invalidatePatientUploads(upload.patientId);
        }
        // Invalidate storage usage as it has changed
        this.storageUsageLoaded.set(false);
      })
    );
  }

  /**
   * Update an existing upload
   *
   * @param uploadId - The upload ID
   * @param update - The updated upload data
   * @returns Observable<boolean>
   */
  updateUpload(uploadId: number, update: UpdateUpload): Observable<boolean> {
    return this.uploadRepo.updateFile(uploadId, update).pipe(
      tap(success => {
        if (success) {
          // Update the upload in all patient caches where it exists
          this.updateUploadInCache(uploadId, update);
        }
      })
    );
  }

  /**
   * Delete an upload
   *
   * @param uploadId - The upload ID
   * @param patientId - Optional patient ID to update specific cache
   * @returns Observable<boolean>
   */
  deleteUpload(uploadId: number, patientId?: number): Observable<boolean> {
    return this.uploadRepo.deleteFile(uploadId).pipe(
      tap(success => {
        if (success) {
          // Remove from cache
          this.removeUploadFromCache(uploadId, patientId);
          // Invalidate storage usage
          this.storageUsageLoaded.set(false);
        }
      })
    );
  }

  // ============================================================
  // Tag Methods
  // ============================================================

  /**
   * Query tags for autocomplete
   * Also caches results for quick access
   *
   * @param query - Search query
   * @returns Observable<BaseTag[]>
   */
  queryTags(query: string): Observable<BaseTag[]> {
    return this.patientUploadsRepo.queryTags(query).pipe(
      tap(tags => this.addToRecentTags(tags))
    );
  }

  // ============================================================
  // WebSocket Integration
  // ============================================================

  /**
   * Subscribe to WebSocket events for a patient
   * Call this when navigating to a patient's uploads view
   *
   * @param patientId - The patient ID
   * @param channelName - The WebSocket channel name
   */
  subscribeToPatientEvents(patientId: number, channelName: string): void {
    if (this.subscribedPatientChannels.has(channelName)) {
      return;
    }

    this.subscribedPatientChannels.add(channelName);

    // Upload complete - refresh uploads and storage
    this.websocket.on(channelName, Events.UploadUploadComplete)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.invalidatePatientUploads(patientId);
        this.loadPatientUploads(patientId, [], true).subscribe();
        this.loadStorageUsage(true).subscribe();
      });

    // Upload deleted - refresh uploads and storage
    this.websocket.on(channelName, Events.UploadUploadDeleted)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.invalidatePatientUploads(patientId);
        this.loadPatientUploads(patientId, [], true).subscribe();
        this.loadStorageUsage(true).subscribe();
      });

    // Upload updated - refresh uploads
    this.websocket.on(channelName, Events.UploadUploadUpdated)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadPatientUploads(patientId, [], true).subscribe();
      });
  }

  /**
   * Subscribe to practice-level WebSocket events
   * Call this during app initialization
   *
   * @param channelName - The practice WebSocket channel name
   */
  subscribeToPracticeEvents(channelName: string): void {
    if (this.practiceChannelName === channelName) {
      return;
    }

    this.practiceChannelName = channelName;

    // Practice-wide upload events affect storage
    this.websocket.on(channelName, Events.UploadUploadComplete)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadStorageUsage(true).subscribe();
      });

    this.websocket.on(channelName, Events.UploadUploadDeleted)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadStorageUsage(true).subscribe();
      });
  }

  /**
   * Unsubscribe from patient WebSocket events
   * Note: With takeUntilDestroyed, this mainly cleans up tracking
   *
   * @param channelName - The WebSocket channel name
   */
  unsubscribeFromPatientEvents(channelName: string): void {
    this.subscribedPatientChannels.delete(channelName);
  }

  // ============================================================
  // Cache Management
  // ============================================================

  /**
   * Invalidate patient uploads cache
   *
   * @param patientId - The patient ID
   */
  invalidatePatientUploads(patientId: number): void {
    const currentMap = new Map(this._patientUploadsMap());
    currentMap.delete(patientId);
    this._patientUploadsMap.set(currentMap);
  }

  /**
   * Invalidate all caches
   * Call on logout or practice switch
   */
  clear(): void {
    this._patientUploadsMap.set(new Map());
    this.storageUsage.set(null);
    this.recentTags.set([]);
    this.storageUsageLoaded.set(false);
    this.loadingStorageUsage.set(false);
    this.loadingPatientUploads.set(new Map());
    this.subscribedPatientChannels.clear();
    this.practiceChannelName = '';
  }

  // ============================================================
  // Private Helper Methods
  // ============================================================

  private setPatientUploads(patientId: number, uploads: Upload[]): void {
    const currentMap = new Map(this._patientUploadsMap());
    currentMap.set(patientId, uploads);
    this._patientUploadsMap.set(currentMap);
  }

  private setPatientUploadsLoading(patientId: number, loading: boolean): void {
    const currentMap = new Map(this.loadingPatientUploads());
    if (loading) {
      currentMap.set(patientId, true);
    } else {
      currentMap.delete(patientId);
    }
    this.loadingPatientUploads.set(currentMap);
  }

  private filterUploadsByTags(uploads: Upload[], tags: string[]): Upload[] {
    if (tags.length === 0) return uploads;
    return uploads.filter(upload =>
      tags.every(tag => upload.tags.some(t => t.tag === tag))
    );
  }

  private updateUploadInCache(uploadId: number, update: UpdateUpload): void {
    const currentMap = new Map(this._patientUploadsMap());

    currentMap.forEach((uploads, patientId) => {
      const index = uploads.findIndex(u => u.id === uploadId);
      if (index > -1) {
        const newUploads = [...uploads];
        newUploads[index] = {
          ...newUploads[index],
          fileName: update.fileName,
          tags: update.tags,
          documentDate: update.documentDate
        };
        currentMap.set(patientId, newUploads);
      }
    });

    this._patientUploadsMap.set(currentMap);
  }

  private removeUploadFromCache(uploadId: number, patientId?: number): void {
    const currentMap = new Map(this._patientUploadsMap());

    if (patientId !== undefined) {
      const uploads = currentMap.get(patientId);
      if (uploads) {
        currentMap.set(patientId, uploads.filter(u => u.id !== uploadId));
      }
    } else {
      // Search all caches
      currentMap.forEach((uploads, pid) => {
        const filtered = uploads.filter(u => u.id !== uploadId);
        if (filtered.length !== uploads.length) {
          currentMap.set(pid, filtered);
        }
      });
    }

    this._patientUploadsMap.set(currentMap);
  }

  private addToRecentTags(tags: BaseTag[]): void {
    const current = this.recentTags();
    const newTags = tags.filter(t => !current.some(c => c.id === t.id));
    const combined = [...newTags, ...current].slice(0, 50);
    this.recentTags.set(combined);
  }
}
